import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { 
  HomeIcon, 
  ChartBarIcon, 
  ArrowTrendingUpIcon,
  ClockIcon,
  SparklesIcon,
  MapIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { CityMapView } from '../components/CityMapView';

interface MarketStats {
  totalListings: number;
  newListings: number;
  soldListings: number;
  medianPrice: number;
  averagePrice: number;
  medianListPrice: number;
  avgDaysOnMarket: number;
  saleToListRatio: string;
  priceRange: {
    min: number;
    max: number;
    under500k: number;
    under750k: number;
    under1m: number;
    over1m: number;
  };
  propertyTypes: { [key: string]: number };
  neighborhoods: string[];
  recentListings: Array<{
    mlsNumber: string;
    address: string;
    price: number;
    bedrooms: number;
    bathrooms: number;
    squareFeet: number;
    photo: string;
    daysOnMarket: number;
  }>;
  marketTrends: {
    inventory: {
      dates: string[];
      active: number[];
      sold: number[];
    };
    pricing: {
      dates: string[];
      median: number[];
      average: number[];
    };
    monthsOfInventory: string;
    marketType: string;
  };
  lastUpdated?: string;
  marketHealth?: {
    marketType: string;
    monthsOfInventory: string;
  };
}

const CHART_COLORS = {
  primary: '#2563EB',
  secondary: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  neutral: '#6B7280'
};

export const CityLandingV2: React.FC = () => {
  const { city } = useParams<{ city: string }>();
  const navigate = useNavigate();
  const [stats, setStats] = useState<MarketStats | null>(null);
  const [loading, setLoading] = useState(true);
  
  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : '';

  useEffect(() => {
    const fetchCityData = async () => {
      if (!city) return;
      
      try {
        const response = await axios.get(`/api/mls-portal/cities/${city}/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching city data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCityData();
  }, [city]);

  const handleAISearch = () => {
    navigate(`/search?city=${city}&source=city-page`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading market data...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">Unable to load market data</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const priceDistributionData = [
    { name: 'Under $500K', value: stats.priceRange.under500k, color: CHART_COLORS.primary },
    { name: '$500K-$750K', value: stats.priceRange.under750k - stats.priceRange.under500k, color: CHART_COLORS.secondary },
    { name: '$750K-$1M', value: stats.priceRange.under1m - stats.priceRange.under750k, color: CHART_COLORS.warning },
    { name: 'Over $1M', value: stats.priceRange.over1m, color: CHART_COLORS.danger }
  ];

  const inventoryTrendData = stats.marketTrends.inventory.dates.map((date, index) => ({
    date: new Date(date).toLocaleDateString('en-US', { month: 'short' }),
    active: stats.marketTrends.inventory.active[index],
    sold: stats.marketTrends.inventory.sold[index]
  })).reverse();

  const priceTrendData = stats.marketTrends.pricing.dates.map((date, index) => ({
    date: new Date(date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    price: stats.marketTrends.pricing.median[index]
  })).reverse();

  return (
    <>
      <Helmet>
        <title>{`Homes for Sale in ${cityName}, AZ | ${stats.totalListings} Properties | Chat.RealEstate`}</title>
        <meta name="description" content={`Search ${stats.totalListings} homes for sale in ${cityName}, Arizona. Median price $${stats.medianPrice.toLocaleString()}. Use AI-powered search to find your perfect home.`} />
        <link rel="canonical" href={`https://chat.realestate/homes-for-sale/${city}`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="text-2xl font-bold text-blue-600">
                chat.realestate
              </Link>
              <div className="hidden md:flex items-center space-x-4">
                <input 
                  type="text" 
                  placeholder="Ask me anything about real estate..." 
                  className="px-4 py-2 border border-gray-300 rounded-lg w-96"
                  onFocus={handleAISearch}
                />
                <button className="px-4 py-2 text-gray-700 hover:text-gray-900">Buy</button>
                <button className="px-4 py-2 text-gray-700 hover:text-gray-900">Sell</button>
                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  AI Assistant
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white">
          <div className="max-w-7xl mx-auto px-4 py-16 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Homes for Sale in {cityName}, AZ
            </h1>
            <p className="text-xl mb-8 opacity-90">
              {stats.totalListings.toLocaleString()} properties available • Updated {new Date(stats.lastUpdated || Date.now()).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </p>
            <button 
              onClick={handleAISearch}
              className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition transform hover:scale-105 shadow-lg"
            >
              <SparklesIcon className="w-5 h-5 inline mr-2" />
              Search with AI Assistant
            </button>
            
            {/* Quick Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <div className="text-2xl font-bold">${(stats.medianPrice / 1000).toFixed(0)}K</div>
                <div className="text-sm opacity-75">Median Price</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <div className="text-2xl font-bold">{stats.avgDaysOnMarket}</div>
                <div className="text-sm opacity-75">Days on Market</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <div className="text-2xl font-bold">{stats.newListings}</div>
                <div className="text-sm opacity-75">New This Month</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4">
                <div className="text-2xl font-bold">{stats.marketTrends.marketType}</div>
                <div className="text-sm opacity-75">Market Type</div>
              </div>
            </div>
          </div>
        </section>

        {/* Market Overview Dashboard */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <h2 className="text-3xl font-bold mb-8">Market Overview</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Market Snapshot */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <ChartBarIcon className="w-5 h-5 mr-2 text-blue-600" />
                Market Snapshot
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={[
                    { name: 'Active', value: stats.totalListings },
                    { name: 'New', value: stats.newListings },
                    { name: 'Sold', value: stats.soldListings }
                  ]}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill={CHART_COLORS.primary} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div>
                  <div className="text-2xl font-bold text-gray-800">{stats.totalListings}</div>
                  <div className="text-sm text-gray-600">Active</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">{stats.newListings}</div>
                  <div className="text-sm text-gray-600">New</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-600">{stats.soldListings}</div>
                  <div className="text-sm text-gray-600">Sold</div>
                </div>
              </div>
            </div>

            {/* Price Trends */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <ArrowTrendingUpIcon className="w-5 h-5 mr-2 text-green-600" />
                Price Trends
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={priceTrendData}>
                    <XAxis dataKey="date" />
                    <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                    <Tooltip formatter={(value: number) => `$${value.toLocaleString()}`} />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke={CHART_COLORS.primary} 
                      strokeWidth={3}
                      dot={{ fill: CHART_COLORS.primary }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-between mt-4">
                <div>
                  <div className="text-2xl font-bold text-gray-800">${(stats.medianPrice / 1000).toFixed(0)}K</div>
                  <div className="text-sm text-gray-600">Median Sale Price</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">+5.2%</div>
                  <div className="text-sm text-gray-600">Year over Year</div>
                </div>
              </div>
            </div>

            {/* Market Dynamics */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <ClockIcon className="w-5 h-5 mr-2 text-orange-600" />
                Market Dynamics
              </h3>
              <div className="text-center py-8">
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-32 h-32">
                    <circle
                      className="text-gray-200"
                      strokeWidth="10"
                      stroke="currentColor"
                      fill="transparent"
                      r="56"
                      cx="64"
                      cy="64"
                    />
                    <circle
                      className="text-blue-600"
                      strokeWidth="10"
                      strokeDasharray={`${(stats.avgDaysOnMarket / 60) * 351.86} 351.86`}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="56"
                      cx="64"
                      cy="64"
                      transform="rotate(-90 64 64)"
                    />
                  </svg>
                  <div className="absolute">
                    <div className="text-3xl font-bold">{stats.avgDaysOnMarket}</div>
                    <div className="text-sm text-gray-600">Days</div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-800">{stats.saleToListRatio}</div>
                  <div className="text-sm text-gray-600">Sale to List</div>
                </div>
                <div className="text-center">
                  <div className="text-xl font-bold text-gray-800">{stats.marketTrends.monthsOfInventory}</div>
                  <div className="text-sm text-gray-600">Months Supply</div>
                </div>
              </div>
            </div>

            {/* Price Distribution */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4 flex items-center">
                <HomeIcon className="w-5 h-5 mr-2 text-purple-600" />
                Price Distribution
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={priceDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {priceDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {priceDistributionData.map((item) => (
                  <div key={item.name} className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm">{item.name}: {item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Interactive Map Section */}
        <section className="bg-gray-100 py-12">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Explore {cityName} Neighborhoods</h2>
            <div className="bg-white rounded-lg shadow-lg p-4">
              <div className="h-96 rounded-lg overflow-hidden">
                <CityMapView 
                  city={city || 'phoenix'} 
                  properties={stats.recentListings}
                  onPropertyClick={(mlsNumber) => navigate(`/property/${mlsNumber}`)}
                />
              </div>
              <div className="mt-4 text-center">
                <button 
                  onClick={handleAISearch}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                >
                  View All {cityName} Properties on Map
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Trending Properties */}
        <section className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold">New Listings in {cityName}</h2>
            <Link 
              to={`/search?city=${city}&sort=newest`}
              className="text-blue-600 hover:text-blue-800 font-semibold flex items-center"
            >
              See All <ArrowRightIcon className="w-4 h-4 ml-1" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.recentListings.slice(0, 4).map((listing) => (
              <Link 
                key={listing.mlsNumber}
                to={`/property/${listing.mlsNumber}`}
                className="bg-white rounded-lg shadow hover:shadow-xl transition transform hover:-translate-y-1"
              >
                <div className="relative">
                  <img 
                    src={listing.photo} 
                    alt={listing.address}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                  {listing.daysOnMarket < 7 && (
                    <span className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-sm font-semibold">
                      NEW
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="text-2xl font-bold text-gray-800">${listing.price.toLocaleString()}</div>
                  <div className="text-gray-600 font-medium mt-1">{listing.address}</div>
                  <div className="text-gray-500 text-sm mt-2">
                    {listing.bedrooms} beds • {listing.bathrooms} baths • {listing.squareFeet.toLocaleString()} sqft
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Popular Neighborhoods */}
        <section className="bg-gray-100 py-12">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Popular {cityName} Neighborhoods</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {stats.neighborhoods.slice(0, 6).map((neighborhood) => (
                <Link 
                  key={neighborhood}
                  to={`/homes-for-sale/${city}/${neighborhood.toLowerCase().replace(/\s+/g, '-')}`}
                  className="bg-white rounded-lg shadow hover:shadow-lg transition p-6"
                >
                  <h3 className="text-xl font-semibold mb-2">{neighborhood}</h3>
                  <p className="text-gray-600">View available homes</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* AI Assistant CTA */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-700 py-16">
          <div className="max-w-4xl mx-auto px-4 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">
              Find Your Perfect {cityName} Home with AI
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Our AI assistant understands natural language and helps you find homes that match your lifestyle and budget
            </p>
            <button 
              onClick={handleAISearch}
              className="px-8 py-4 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition transform hover:scale-105 shadow-lg"
            >
              Start AI-Powered Search
            </button>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-12 max-w-2xl mx-auto">
              <div className="flex items-center justify-center">
                <span className="text-green-400 mr-2">✓</span>
                Natural Language
              </div>
              <div className="flex items-center justify-center">
                <span className="text-green-400 mr-2">✓</span>
                Real-time MLS Data
              </div>
              <div className="flex items-center justify-center">
                <span className="text-green-400 mr-2">✓</span>
                Personalized Results
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-gray-300 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center">
              <p className="text-sm">
                © 2024 Arizona Regional Multiple Listing Service, Inc. All rights reserved.
              </p>
              <p className="text-sm mt-2">
                All information should be verified by the recipient and none is guaranteed as accurate by ARMLS.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};